import { useCallback, useEffect, useRef, useState } from "react";
import type { ActivityResponse, ActivityRow } from "@shared/types";
import { fetchActivity, ApiError } from "./api-client";

interface UseActivityState {
  rows: ActivityRow[];
  nextCursor: string | null;
  error: ApiError | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

const FIVE_MINUTES = 5 * 60 * 1000;

export function useActivity(
  limit: number = 20,
  pollMs: number = FIVE_MINUTES
): UseActivityState {
  const [data, setData] = useState<ActivityResponse | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const mountedRef = useRef<boolean>(true);

  const load = useCallback(async () => {
    try {
      const result = await fetchActivity(undefined, limit);
      if (!mountedRef.current) return;
      setData(result);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      if (err instanceof ApiError) setError(err);
      else setError(new ApiError(0, "unknown", "unknown error"));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    const id = window.setInterval(() => {
      void load();
    }, pollMs);
    return () => {
      mountedRef.current = false;
      window.clearInterval(id);
    };
  }, [load, pollMs]);

  return {
    rows: data?.rows ?? [],
    nextCursor: data?.nextCursor ?? null,
    error,
    loading,
    refetch: load,
  };
}
